import axios from "axios";
import { getToken } from "./auth";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// Axios instance with base URL
const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Automatically attach JWT token
axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear auth data
      const Cookies = require("js-cookie").default;
      Cookies.remove("auth_token");
      Cookies.remove("user");
      // Redirect to login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

/**
 * Get all users
 */
export const getAllUsers = async () => {
  try {
    const res = await axiosInstance.get("/User/GetAllUsers");
    return res.data;
  } catch (err) {
    console.error("Error fetching users:", err);
    throw err;
  }
};

/**
 * Get user by ID
 * @param {string} id
 */
export const getUserById = async (id) => {
  try {
    const res = await axiosInstance.get(`/User/GetUserById?id=${id}`);
    return res.data;
  } catch (err) {
    console.error(`Error fetching user ${id}:`, err);
    throw err;
  }
};

/**
 * Add new user
 * @param {Object} userData (UserDTO schema)
 */
export const addUser = async (userData) => {
  try {
    const res = await axiosInstance.post("/User/AddUser", userData, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res.data;
  } catch (err) {
    console.error("Error adding user:", err.response?.data || err.message);
    throw err;
  }
};

/**
 * Edit user by username
 * @param {string} username
 * @param {Object} userData (UserDTO schema)
 */
export const editUser = async (username, userData) => {
  try {
    const res = await axiosInstance.put(
      `/User/EditUserByUsername?username=${username}`,
      userData
    );
    return res.data;
  } catch (err) {
    console.error(`Error editing user ${username}:`, err);
    throw err;
  }
};

/**
 * Change password for the user
 * @param {Object} userData (UserDTO schema)
 */
export const changePassword = async (userData) => {
  try {
    const res = await axiosInstance.put(`/User/ChangePassword`, userData);
    return res.data;
  } catch (err) {
    console.error(`Error changing password:`, err);
    throw err;
  }
};

/**
 * Delete user by username
 * @param {string} username
 */
export const deleteUser = async (username) => {
  try {
    const res = await axiosInstance.delete(`/User/DeleteUser?id=${username}`);
    return res.data;
  } catch (err) {
    console.error(`Error deleting user ${username}:`, err);
    throw err;
  }
};

/**
 * Get user role by ID
 * @param {number} id
 */
export const getUserRoleById = async (id) => {
  try {
    const res = await axiosInstance.get(`/User/GetUserRoleById?id=${id}`);
    return res.data;
  } catch (err) {
    console.error(`Error fetching user role ${id}:`, err);
    throw err;
  }
};

/**
 * Get all user roles
 */
export const getUserRoles = async () => {
  try {
    const res = await axiosInstance.get("/User/GetUserRoles");
    return res.data;
  } catch (err) {
    console.error("Error fetching user roles:", err);
    throw err;
  }
};

/**
 * Get controller worker by ID
 * @param {string} username
 */
export const getControllerByWorkerId = async (username) => {
  try {
    const res = await axiosInstance.get(
      `/User/GetControllerByWorkerName?workerName=${username}`
    );
    return res.data;
  } catch (err) {
    console.error(`Error fetching controller for worker ${username}:`, err);
    throw err;
  }
};

/**
 * Get user role by ID
 * @param {string}
 */
export const getWorkersByController = async (username) => {
  try {
    const res = await axiosInstance.get(
      `/User/GetWorkersByController?controllerId=${username}`
    );
    return res.data;
  } catch (err) {
    console.error(`Error fetching workers for controller ${username}:`, err);
    throw err;
  }
};

/**
 * Add a worker to a controller
 * @param {string} workerName - The name of the worker to add
 * @param {string} controllerId - The ID of the controller
 * @returns {Promise<any>} - The response data from the API
 */
export const addWorkerToController = async (workerName, controllerId) => {
  try {
    const res = await axiosInstance.post(`/User/AddWorkerToController`, null, {
      params: {
        workerName,
        controllerId,
      },
    });
    return res.data;
  } catch (err) {
    console.error(
      `Error adding worker '${workerName}' to controller '${controllerId}':`,
      err
    );
    throw err;
  }
};

/**
 * Get the user's company
 */
export const getCompanies = async () => {
  try {
    const res = await axiosInstance.get("/Company/GetCompanies");
    return res.data;
  } catch (err) {
    console.error("Error fetching companies:", err);
    throw err;
  }
};

/**
 * Get all customers
 */
export const getCustomers = async () => {
  try {
    const res = await axiosInstance.get("/Customer/GetCustomers");
    return res.data;
  } catch (err) {
    console.error("Error fetching customers:", err);
    throw err;
  }
};

/**
 * Get all users' document type allowances
 */
export const getUsersAllowance = async () => {
  try {
    const res = await axiosInstance.get(
      "/DocumentTypeAllowance/GetUsersAllowance"
    );
    return res.data;
  } catch (err) {
    console.error("Error fetching users allowance:", err);
    throw err;
  }
};

/**
 * Get all document types
 */
export const getDocumentTypes = async () => {
  try {
    const res = await axiosInstance.get(
      "/DocumentTypeAllowance/GetDocumentTypes"
    );
    return res.data;
  } catch (err) {
    console.error("Error fetching document types:", err);
    throw err;
  }
};

/**
 * Add a new user document allowance
 * @param {Object} allowanceData
 * {
 *   "companyCode": "string",
 *   "username": "string",
 *   "documentType": 0,
 *   "locationCode": "string"
 * }
 */
export const addUserAllowance = async (allowanceData) => {
  try {
    const res = await axiosInstance.post(
      "/DocumentTypeAllowance/AddNewAllowance",
      allowanceData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return res.data;
  } catch (err) {
    console.error(
      "Error adding user allowance:",
      err.response?.data || err.message
    );
    throw err;
  }
};
